import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { approveMemberRequest, rejectMemberRequest } from '../services/authService';
import './Requests.css'; // Reuse Requests styles

const MemberRequests = () => {
  const { orgDomain, isOrgAdminUser, isTeamMember, isOrgMember } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [selectedRequests, setSelectedRequests] = useState(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const requestsRef = collection(db, 'orgMemberRequests');
      const requestsQuery = query(
        requestsRef,
        where('domain', '==', orgDomain),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );
      const requestsSnapshot = await getDocs(requestsQuery);

      const requestsData = requestsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || null,
      }));

      setRequests(requestsData);
    } catch (err) {
      console.error('Error loading member requests:', err);
      setError('Failed to load requests. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [orgDomain]);

  useEffect(() => {
    if (orgDomain && (isOrgAdminUser || isTeamMember || isOrgMember)) {
      loadRequests();
    }
  }, [orgDomain, isOrgAdminUser, isTeamMember, isOrgMember, loadRequests]);

  if (!isOrgAdminUser && !isTeamMember && !isOrgMember) {
    return (
      <div className="requests-page">
        <div className="page-header">
          <h1>Member Requests</h1>
        </div>
        <div className="error-state">
          <p>You must be an admin or team member to view member requests.</p>
        </div>
      </div>
    );
  }

  const handleApprove = async (request) => {
    if (!window.confirm(`Approve request from ${request.email} to join this organization?`)) {
      return;
    }

    try {
      setProcessingId(request.id);
      await approveMemberRequest(request.id, request);
      await loadRequests(); // Reload requests
    } catch (err) {
      console.error('Error approving member request:', err);
      alert('Failed to approve request. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (request) => {
    if (!window.confirm(`Reject request from ${request.email} to join this organization?`)) {
      return;
    }

    try {
      setProcessingId(request.id);
      await rejectMemberRequest(request.id, request);
      await loadRequests(); // Reload requests
      setSelectedRequests(new Set());
    } catch (err) {
      console.error('Error rejecting member request:', err);
      alert('Failed to reject request. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedRequests.size === 0) return;

    if (!window.confirm(`Approve ${selectedRequests.size} member request(s)?`)) {
      return;
    }

    try {
      setIsBulkProcessing(true);
      const selectedArray = Array.from(selectedRequests);
      const requestsToApprove = requests.filter(r => selectedArray.includes(r.id));

      // Process all approvals
      await Promise.all(
        requestsToApprove.map(request => approveMemberRequest(request.id, request))
      );

      await loadRequests(); // Reload requests
      setSelectedRequests(new Set());
      alert(`Successfully approved ${requestsToApprove.length} request(s)!`);
    } catch (err) {
      console.error('Error bulk approving requests:', err);
      alert('Failed to approve some requests. Please try again.');
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const handleToggleSelect = (requestId) => {
    const newSelected = new Set(selectedRequests);
    if (newSelected.has(requestId)) {
      newSelected.delete(requestId);
    } else {
      newSelected.add(requestId);
    }
    setSelectedRequests(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedRequests.size === requests.length) {
      setSelectedRequests(new Set());
    } else {
      setSelectedRequests(new Set(requests.map(r => r.id)));
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="requests-page">
        <div className="page-header">
          <h1>Member Requests</h1>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading requests...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="requests-page">
        <div className="page-header">
          <h1>Member Requests</h1>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={loadRequests} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="requests-page">
      <div className="page-header">
        <h1>Member Requests</h1>
        {orgDomain && <p className="org-domain">Domain: {orgDomain}</p>}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {requests.length > 0 && (
            <>
              <button
                onClick={handleSelectAll}
                className="refresh-button"
                style={{ fontSize: '14px', padding: '8px 16px' }}
              >
                {selectedRequests.size === requests.length ? 'Deselect All' : 'Select All'}
              </button>
              {selectedRequests.size > 0 && (
                <button
                  onClick={handleBulkApprove}
                  className="approve-button"
                  disabled={isBulkProcessing}
                  style={{ fontSize: '14px', padding: '8px 16px' }}
                >
                  {isBulkProcessing ? 'Processing...' : `Approve ${selectedRequests.size}`}
                </button>
              )}
            </>
          )}
          <button onClick={loadRequests} className="refresh-button">
            🔄 Refresh
          </button>
        </div>
      </div>

      {requests.length === 0 ? (
        <div className="empty-state">
          <p>No pending member requests</p>
        </div>
      ) : (
        <div className="requests-list">
          {requests.map((request) => {
            const isSelected = selectedRequests.has(request.id);
            return (
              <div key={request.id} className="request-card" style={{
                border: isSelected ? '2px solid #2ab3c0' : '1px solid #e5e7eb',
                backgroundColor: isSelected ? '#f0f9ff' : 'white',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleToggleSelect(request.id)}
                    style={{ marginTop: '4px', cursor: 'pointer' }}
                  />
                  <div style={{ flex: 1 }}>
                    <div className="request-header">
                      <div>
                        <h3>{request.name || 'Unknown'}</h3>
                        <p className="request-email">{request.email}</p>
                      </div>
                      <div className="request-domain">
                        <span className="domain-badge">{request.domain}</span>
                      </div>
                    </div>
                    <div className="request-details">
                      <p className="request-date">
                        Requested: {formatDate(request.createdAt)}
                      </p>
                    </div>
                    <div className="request-actions">
                      <button
                        className="approve-button"
                        onClick={() => handleApprove(request)}
                        disabled={processingId === request.id || isBulkProcessing}
                      >
                        {processingId === request.id ? 'Processing...' : 'Approve'}
                      </button>
                      <button
                        className="reject-button"
                        onClick={() => handleReject(request)}
                        disabled={processingId === request.id || isBulkProcessing}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MemberRequests;
